import type { BusinessCredential } from "@/types/directory";

/**
 * Certification, accreditation and collaboration logos shown as a card strip
 * (maximum of five) on a business profile.
 *
 * Covered for every seeded business — keyed by business slug. The strip is a
 * paid-plan feature, so it is only rendered for Business / Premium listings.
 */
export const businessCredentials: Record<string, BusinessCredential[]> = {
  // ── South Canara ────────────────────────────────────────────────────────────
  "sree-gokul-pure-veg-restaurant": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "kadri-grand-hotel-and-suites": [
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "green", label: "Green Certified", icon: "ri-leaf-line" },
    { id: "mmt", label: "MakeMyTrip Partner", icon: "ri-flight-takeoff-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
  ],
  "coastal-care-multispecialty-hospital": [
    { id: "nabl", label: "NABL Lab", icon: "ri-microscope-line" },
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "nhm", label: "NHM Empanelled", icon: "ri-heart-pulse-line" },
    { id: "insurance", label: "Cashless Insurance", icon: "ri-shield-check-line" },
  ],
  "mangala-builders-and-developers": [
    { id: "rera", label: "RERA Registered", icon: "ri-building-2-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "igbc", label: "IGBC Member", icon: "ri-plant-line" },
    { id: "crii", label: "CREDAI Member", icon: "ri-community-line" },
    { id: "loan", label: "Loan Assisted", icon: "ri-bank-line" },
  ],
  "puttur-prime-motors": [
    { id: "oem", label: "OEM Authorised", icon: "ri-steering-2-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "insurance", label: "Insurance Desk", icon: "ri-shield-check-line" },
    { id: "finance", label: "Finance Partner", icon: "ri-bank-card-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
  ],
  "surathkal-fashion-hub": [
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "moodabidri-green-grocers": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "delivery", label: "Home Delivery", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "udupi-sagar-grand": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "zomato", label: "Zomato Partner", icon: "ri-store-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],

  // ── Udupi ───────────────────────────────────────────────────────────────────
  "manipal-wellness-hospital": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "nabl", label: "NABL Lab", icon: "ri-microscope-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "ayushman", label: "Ayushman Bharat", icon: "ri-first-aid-kit-line" },
    { id: "insurance", label: "Cashless Insurance", icon: "ri-shield-check-line" },
  ],
  "kundapura-lighthouse-beach-resort": [
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "booking", label: "Booking.com Partner", icon: "ri-flight-takeoff-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
  ],
  "karkala-vidya-coaching-academy": [
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "govt", label: "Govt Recognised", icon: "ri-government-line" },
    { id: "neet", label: "NEET & JEE Coaching", icon: "ri-book-open-line" },
    { id: "instalments", label: "Fee Instalments", icon: "ri-bank-card-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-file-list-3-line" },
  ],
  "udupi-coastal-electronics": [
    { id: "bis", label: "BIS Certified", icon: "ri-shield-check-line" },
    { id: "warranty", label: "OEM Warranty", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "emi", label: "EMI Available", icon: "ri-bank-card-line" },
    { id: "service", label: "Authorised Service", icon: "ri-tools-line" },
  ],
  "karwar-blue-bay-electronics": [
    { id: "bis", label: "BIS Certified", icon: "ri-shield-check-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "warranty", label: "OEM Warranty", icon: "ri-award-line" },
    { id: "emi", label: "EMI Available", icon: "ri-bank-card-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "sirsi-spice-cafe": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "bhatkal-coastal-motors": [
    { id: "oem", label: "OEM Authorised", icon: "ri-steering-2-line" },
    { id: "insurance", label: "Insurance Desk", icon: "ri-shield-check-line" },
    { id: "finance", label: "Finance Partner", icon: "ri-bank-card-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "rsa", label: "Roadside Assistance", icon: "ri-tools-line" },
  ],
  "ankola-green-grocers": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "delivery", label: "Home Delivery", icon: "ri-motorbike-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],

  // ── Retail & lifestyle ──────────────────────────────────────────────────────
  "anantha-jewellers": [
    { id: "bis", label: "BIS Hallmark", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "gia", label: "GIA Certified Stones", icon: "ri-shield-star-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
  ],
  "bandhan-sweets-and-bakery": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cake-3-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "coastal-dental-studio": [
    { id: "dci", label: "DCI Registered", icon: "ri-stethoscope-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "sterile", label: "Sterilisation Certified", icon: "ri-shield-check-line" },
    { id: "digital", label: "Digital Imaging", icon: "ri-microscope-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
  ],
  "emerald-eye-care-centre": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "screening", label: "Digital Eye Screening", icon: "ri-microscope-line" },
    { id: "optom", label: "Registered Optometrists", icon: "ri-stethoscope-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
  ],
  "fresh-basket-supermarket": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "delivery", label: "Home Delivery", icon: "ri-motorbike-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "gitanjali-silk-house": [
    { id: "silkmark", label: "Silk Mark Certified", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "handloom", label: "Handloom Certified", icon: "ri-store-3-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],

  // ── Healthcare & wellness ───────────────────────────────────────────────────
  "hegde-ayurvedic-centre": [
    { id: "ayush", label: "AYUSH Registered", icon: "ri-leaf-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "gmp", label: "GMP Certified", icon: "ri-shield-check-line" },
    { id: "practitioner", label: "Ayurveda Practitioner", icon: "ri-stethoscope-line" },
    { id: "pancha", label: "Panchakarma Certified", icon: "ri-heart-pulse-line" },
  ],
  "indraprastha-family-restaurant": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "malpe-sea-foods": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "coldchain", label: "Cold Chain Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "prasad-sweets-and-namkeen": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cake-3-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "ratnakar-builders": [
    { id: "rera", label: "RERA Registered", icon: "ri-building-2-line" },
    { id: "crii", label: "CREDAI Member", icon: "ri-community-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "loan", label: "Loan Assisted", icon: "ri-bank-line" },
    { id: "igbc", label: "IGBC Member", icon: "ri-plant-line" },
  ],
  "tulunadu-motors": [
    { id: "oem", label: "OEM Authorised", icon: "ri-steering-2-line" },
    { id: "insurance", label: "Insurance Desk", icon: "ri-shield-check-line" },
    { id: "finance", label: "Finance Partner", icon: "ri-bank-card-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
  ],
  "kadamba-multi-speciality-pharmacy": [
    { id: "drug", label: "Drug Licensed", icon: "ri-first-aid-kit-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "coldchain", label: "Cold Storage Certified", icon: "ri-shield-check-line" },
    { id: "delivery", label: "Home Delivery", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "netravathi-diagnostics-and-scan-centre": [
    { id: "nabl", label: "NABL Accredited", icon: "ri-microscope-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "nabh", label: "NABH Certified", icon: "ri-hospital-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
    { id: "cashless", label: "Cashless Insurance", icon: "ri-shield-check-line" },
  ],
  "mangaluru-gold-palace": [
    { id: "bis", label: "BIS Hallmark", icon: "ri-award-line" },
    { id: "gia", label: "GIA Certified Stones", icon: "ri-shield-star-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
  ],
  "sharada-tailors-and-boutique": [
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "handloom", label: "Handloom Certified", icon: "ri-store-3-line" },
    { id: "homeservice", label: "Home Service", icon: "ri-home-4-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "puttur-sagar-bakes": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cake-3-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "sri-dharmasthala-homestay": [
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "mmt", label: "MakeMyTrip Partner", icon: "ri-flight-takeoff-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
  ],
  "puttur-care-polyclinic": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "nhm", label: "NHM Empanelled", icon: "ri-heart-pulse-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
    { id: "lab", label: "NABL Lab Partner", icon: "ri-microscope-line" },
  ],
  "surathkal-beach-cafe": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cup-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "zomato", label: "Zomato Partner", icon: "ri-store-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "nit-road-books-and-stationery": [
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "bulk", label: "Bulk Orders", icon: "ri-book-open-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "surathkal-mobile-world": [
    { id: "bis", label: "BIS Certified", icon: "ri-shield-check-line" },
    { id: "warranty", label: "OEM Warranty", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "emi", label: "EMI Available", icon: "ri-bank-card-line" },
    { id: "service", label: "Authorised Service", icon: "ri-smartphone-line" },
  ],
  "moodabidri-care-hospital": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "nabl", label: "NABL Lab", icon: "ri-microscope-line" },
    { id: "ayushman", label: "Ayushman Bharat", icon: "ri-first-aid-kit-line" },
    { id: "nhm", label: "NHM Empanelled", icon: "ri-heart-pulse-line" },
    { id: "insurance", label: "Cashless Insurance", icon: "ri-shield-check-line" },
  ],
  "jain-heritage-sweets": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cake-3-line" },
    { id: "pureveg", label: "Pure Veg Certified", icon: "ri-leaf-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "moodabidri-silk-center": [
    { id: "silkmark", label: "Silk Mark Certified", icon: "ri-award-line" },
    { id: "handloom", label: "Handloom Certified", icon: "ri-store-3-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "udupi-grand-bakery": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cake-3-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "sri-krishna-homestay-udupi": [
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "booking", label: "Booking.com Partner", icon: "ri-flight-takeoff-line" },
    { id: "meals", label: "Home Cooked Meals", icon: "ri-restaurant-2-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "udupi-study-point-coaching": [
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "govt", label: "Govt Recognised", icon: "ri-government-line" },
    { id: "neet", label: "NEET & JEE Coaching", icon: "ri-book-open-line" },
    { id: "instalments", label: "Fee Instalments", icon: "ri-bank-card-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-file-list-3-line" },
  ],
  "manipal-book-house": [
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "academic", label: "Academic Partner", icon: "ri-book-open-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "manipal-dental-care": [
    { id: "dci", label: "DCI Registered", icon: "ri-stethoscope-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "sterile", label: "Sterilisation Certified", icon: "ri-shield-check-line" },
    { id: "digital", label: "Digital Imaging", icon: "ri-microscope-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
  ],

  // ── North Canara & inland ───────────────────────────────────────────────────
  "manipal-cafe-aroma": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cup-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "kundapura-fish-land": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "coldchain", label: "Cold Chain Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "kundapura-beach-homestay": [
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "mmt", label: "MakeMyTrip Partner", icon: "ri-flight-takeoff-line" },
    { id: "meals", label: "Home Cooked Meals", icon: "ri-restaurant-2-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "kundapura-agri-traders": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "bulk", label: "Bulk Supply", icon: "ri-truck-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "karkala-heritage-cafe": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cup-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "zomato", label: "Zomato Partner", icon: "ri-store-3-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "karkala-estate-consultants": [
    { id: "rera", label: "RERA Registered", icon: "ri-building-2-line" },
    { id: "crii", label: "CREDAI Member", icon: "ri-community-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "loan", label: "Loan Assisted", icon: "ri-bank-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
  ],
  "karkala-mobile-zone": [
    { id: "bis", label: "BIS Certified", icon: "ri-shield-check-line" },
    { id: "warranty", label: "OEM Warranty", icon: "ri-award-line" },
    { id: "emi", label: "EMI Available", icon: "ri-bank-card-line" },
    { id: "service", label: "Authorised Service", icon: "ri-smartphone-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "karwar-sea-shore-restaurant": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "karwar-coastal-stay": [
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "mmt", label: "MakeMyTrip Partner", icon: "ri-flight-takeoff-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
  ],
  "karwar-care-hospital": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "nabl", label: "NABL Lab", icon: "ri-microscope-line" },
    { id: "ayushman", label: "Ayushman Bharat", icon: "ri-first-aid-kit-line" },
    { id: "nhm", label: "NHM Empanelled", icon: "ri-heart-pulse-line" },
    { id: "insurance", label: "Cashless Insurance", icon: "ri-shield-check-line" },
  ],
  "sirsi-naturals-ayurveda": [
    { id: "ayush", label: "AYUSH Registered", icon: "ri-leaf-line" },
    { id: "gmp", label: "GMP Certified", icon: "ri-shield-check-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "pancha", label: "Panchakarma Certified", icon: "ri-heart-pulse-line" },
    { id: "herbal", label: "In-house Herbal Products", icon: "ri-plant-line" },
  ],
  "sirsi-spice-market": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "bulk", label: "Bulk Supply", icon: "ri-truck-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "sirsi-green-homestay": [
    { id: "greenkey", label: "Green Key Certified", icon: "ri-leaf-line" },
    { id: "fhra", label: "FHRAI Member", icon: "ri-hotel-line" },
    { id: "booking", label: "Booking.com Partner", icon: "ri-flight-takeoff-line" },
    { id: "meals", label: "Home Cooked Meals", icon: "ri-restaurant-2-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "bhatkal-sea-foods": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "iso22000", label: "ISO 22000", icon: "ri-award-line" },
    { id: "coldchain", label: "Cold Chain Certified", icon: "ri-shield-check-line" },
    { id: "swiggy", label: "Swiggy Partner", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "bhatkal-fashion-mall": [
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "cards", label: "Cards Accepted", icon: "ri-bank-card-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "bhatkal-coastal-clinic": [
    { id: "nabh", label: "NABH Accredited", icon: "ri-hospital-line" },
    { id: "iso9001", label: "ISO 9001:2015", icon: "ri-award-line" },
    { id: "nhm", label: "NHM Empanelled", icon: "ri-heart-pulse-line" },
    { id: "insurance", label: "Insurance Accepted", icon: "ri-bank-card-line" },
    { id: "lab", label: "NABL Lab Partner", icon: "ri-microscope-line" },
  ],
  "ankola-beach-cafe": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-cup-line" },
    { id: "haccp", label: "HACCP Certified", icon: "ri-shield-check-line" },
    { id: "zomato", label: "Zomato Partner", icon: "ri-store-3-line" },
    { id: "gst", label: "GST Registered", icon: "ri-file-list-3-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
  "ankola-honey-and-spice-store": [
    { id: "fssai", label: "FSSAI Licensed", icon: "ri-restaurant-2-line" },
    { id: "organic", label: "Organic Sourced", icon: "ri-leaf-line" },
    { id: "msme", label: "MSME Udyam", icon: "ri-government-line" },
    { id: "delivery", label: "Home Delivery", icon: "ri-motorbike-line" },
    { id: "upi", label: "UPI Accepted", icon: "ri-qr-code-line" },
  ],
};